import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Play, Gamepad2, Palette } from "lucide-react";

const LINKS = [
  { label: "Episodes", href: "#episodes", testid: "nav-link-episodes" },
  { label: "BUBZ+", href: "#bubzplus", testid: "nav-link-bubzplus" },
  { label: "Creator", href: "#creator", testid: "nav-link-creator" },
];

const PAGE_LINKS = [
  { label: "Pop Game", to: "/game", testid: "nav-link-game", icon: Gamepad2 },
  { label: "Coloring", to: "/coloring", testid: "nav-link-coloring", icon: Palette },
];

export const scrollToHash = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -90, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
    } else {
      scrollToHash(href);
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      scrollToHash("#top");
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      className="fixed inset-x-0 top-4 z-50 px-4"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 90, damping: 16, delay: 0.2 }}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between rounded-full border-2 border-white/70 bg-white/80 py-2.5 pl-4 pr-2.5 shadow-[0_10px_40px_rgba(112,193,255,0.35)] backdrop-blur-xl">
        <a href="/" data-testid="nav-logo" onClick={goHome} className="flex items-center gap-2.5">
          <img
            src="/logo.png"
            alt="BUBZ & BUBBLES official logo"
            className="h-12 w-auto"
          />
        </a>

        <div className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => go(e, l.href)}
              className="rounded-full px-3.5 py-2 text-sm font-extrabold text-bubz-ink/70 transition-colors duration-200 hover:bg-bubz-sky/15 hover:text-bubz-ink"
            >
              {l.label}
            </a>
          ))}
          {PAGE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-extrabold transition-colors duration-200 ${
                location.pathname === l.to
                  ? "bg-bubz-sun/40 text-bubz-ink"
                  : "text-bubz-ink/70 hover:bg-bubz-sky/15 hover:text-bubz-ink"
              }`}
            >
              <l.icon size={14} /> {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <motion.a
            data-testid="nav-watch-button"
            href="https://www.youtube.com/@BUBZPLUSSTREAMINGSERVICE"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-bubz-pink px-5 py-2.5 text-sm font-extrabold text-white shadow-[0_6px_20px_rgba(255,117,181,0.5)] sm:flex"
            whileHover={{ scale: 1.06, rotate: -1 }}
            whileTap={{ scale: 0.94 }}
          >
            <Play size={15} fill="currentColor" /> Watch on YouTube
          </motion.a>
          <button
            data-testid="nav-menu-button"
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-bubz-sun/80 text-bubz-ink lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="mx-auto mt-2 max-w-6xl rounded-3xl border-2 border-white/70 bg-white/95 p-4 shadow-xl backdrop-blur-xl lg:hidden"
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={(e) => go(e, l.href)}
                className="block rounded-2xl px-4 py-3 font-display text-lg font-semibold text-bubz-ink hover:bg-bubz-sky/15"
              >
                {l.label}
              </a>
            ))}
            {PAGE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-2xl px-4 py-3 font-display text-lg font-semibold text-bubz-ink hover:bg-bubz-sky/15"
              >
                <l.icon size={18} /> {l.label}
              </Link>
            ))}
            <a
              data-testid="mobile-nav-watch-button"
              href="https://www.youtube.com/@BUBZPLUSSTREAMINGSERVICE"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 rounded-2xl bg-bubz-pink px-4 py-3 font-display text-lg font-semibold text-white"
            >
              <Play size={16} fill="currentColor" /> Watch on YouTube
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
